'use client';
import { cva, type VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import React, { ButtonHTMLAttributes, ReactNode, CSSProperties } from 'react';

const buttonClasses = cva(
  'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary: 'bg-gradient-to-r from-[#ff00cc] to-[#fc4a1a] text-white hover:opacity-90 focus:ring-pink-500',
        secondary: 'bg-background-tertiary text-text-primary hover:bg-opacity-80 focus:ring-gray-500',
        outline: 'border-2 border-border-primary text-text-primary bg-transparent hover:bg-background-tertiary focus:ring-blue-500',
        danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
      },
      size: {
        small: 'text-sm px-3 py-1.5',
        medium: 'text-base px-4 py-2',
        large: 'text-lg px-6 py-3',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  }
)

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonClasses> {
  // Required parameters with defaults
  text?: string;
  text_font_size?: string;
  text_font_family?: string;
  text_font_weight?: string;
  text_line_height?: string;
  text_text_align?: string;
  text_text_transform?: string;
  text_color?: string;
  fill_background?: string;
  border_border_radius?: string;
  effect_box_shadow?: string;
  
  // Optional parameters
  fill_background_color?: string;
  border_border?: string;
  layout_width?: string;
  padding?: string;
  position?: string;
  
  // Standard React props
  children?: ReactNode;
}

const Button = ({
  // Required parameters with defaults
  text = "Contact US",
  text_font_size = "text-xl",
  text_font_family = "Gotham",
  text_font_weight = "400",
  text_line_height = "leading-relaxed",
  text_text_align = "left",
  text_text_transform = "uppercase",
  text_color = "text-text-primary",
  fill_background = "#070E3166",
  border_border_radius = "rounded-sm",
  effect_box_shadow = "shadow-[2px_2px_1px_#fc4a1a]",
  
  // Optional parameters (no defaults)
  fill_background_color,
  border_border,
  layout_width,
  padding,
  position,
  
  // Standard React props
  variant,
  size,
  disabled = false,
  className,
  children,
  onClick,
  type = 'button',
  ...props
}: ButtonProps) => {
  // Safe validation for optional parameters
  const hasValidWidth = layout_width && typeof layout_width === 'string' && layout_width.trim() !== ''
  const hasValidPadding = padding && typeof padding === 'string' && padding.trim() !== ''
  const hasValidPosition = position && typeof position === 'string' && position.trim() !== ''
  const hasValidBorder = border_border && typeof border_border === 'string' && border_border.trim() !== ''
  const hasValidFillColor = fill_background_color && typeof fill_background_color === 'string' && fill_background_color.trim() !== ''

  const optionalClasses = [
    hasValidWidth ? `w-[${layout_width}]` : '',
    hasValidPadding ? `p-[${padding}]` : '',
    hasValidPosition ? position : '',
    hasValidBorder ? `border-[${border_border}]` : '',
  ].filter(Boolean).join(' ')

  // Build custom styles for non-Tailwind properties
  const customStyles: CSSProperties = {
    // Only use inline styles for truly custom values
    ...(text_font_family && !text_font_family.startsWith('font-') && { fontFamily: text_font_family }),
    ...(fill_background && !variant && { background: fill_background }),
    ...(hasValidFillColor && { backgroundColor: fill_background_color }),
  }

  // Build Tailwind classes for styling
  const styleClasses = [
    text_font_size,
    text_font_family.startsWith('font-') ? text_font_family : '',
    text_font_weight,
    text_line_height,
    text_text_align === 'center' ? 'text-center' : text_text_align === 'right' ? 'text-right' : 'text-left',
    text_text_transform === 'uppercase' ? 'uppercase' : text_text_transform === 'lowercase' ? 'lowercase' : '',
    text_color,
    border_border_radius,
    effect_box_shadow,
    // Only apply background if not using variant system
    !variant && !fill_background ? (hasValidFillColor ? '' : 'bg-gradient-to-r from-[#ff00cc] to-[#fc4a1a]') : '',
  ].filter(Boolean).join(' ')

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      event.preventDefault()
      return
    }
    
    if (typeof onClick === 'function') {
      onClick(event)
    }
  }

  return (
    <button data-component-id="src/components/ui/Button.tsx:134:4" data-component-path="src/components/ui/Button.tsx" data-component-line="134" data-component-end-line="149" data-component-file="Button.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22%5Bvar%3Atype%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      type={type}
      disabled={disabled}
      onClick={handleClick}
      style={customStyles}
      className={twMerge(
        buttonClasses({ variant, size }),
        styleClasses,
        optionalClasses,
        className
      )}
      aria-disabled={disabled}
      {...props}
    >
      {children || text}
    </button>
  )
}

export default Button